import { apiClient } from "./apiClient.js"

/****************************************************************************
 * 
 * UTG / Component
 * 
 ***************************************************************************/

export const utgApi = {
    /**
     * Fetch UTG List for a given Business Entity ID
     * @param {string} businessEntityId - The Business Entity ID
     * @param {object} filter - The filter object containing the predicates array
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The UTG list as a Promise
     */
    fetchUtgList: async (businessEntityId, filters, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/components/query`
        const componentsFilter = filters ? JSON.stringify(filters) : '{"Predicates": []}'
        const body = JSON.stringify(componentsFilter).replace("\n", "")
        return await apiClient(
            url,
            accessToken,
            {
                method: 'POST',
                body: body,
                headers: {
                    'clientId': '4'
                }
            },
            async (response) => {
                return await response.json().then(
                    (result) => {
                        return result.serviceView
                    }
                )
            }
        )
    },
    /**
     * Fetch Component for a given Business Entity ID and Component ID. This returns a component record rather than the view item.
     * @param {string} businessEntityId - The Business Entity ID
     * @param {string} componentId - The Component ID
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The Component as a Promise
     */
    fetchComponent: async (businessEntityId, componentId, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/components/${componentId}`
        return await apiClient(
            url,
            accessToken,
            {
                method: "GET"
            },
            async (response) => {
                return await response.json().then(
                    (result) => {
                        return result.component
                    }
                )
            }
        )
    },
    /**
     * Fetch UTG for a given Business Entity ID and Component ID
     * @param {string} businessEntityId - The Business Entity ID
     * @param {string} componentId - The Component ID
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The UTG as a Promise
     */
    fetchUtg: async (businessEntityId, componentId, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/components/query`
        const componentsFilter = `{"Predicates": [{"PropertyName":"ComponentId","Operator":"EQ","Value":"${componentId}","TypeHint": "Integer"}]}`
        const body = JSON.stringify(componentsFilter).replace("\n", "")
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'clientId': '4'
                },
                body: body
            },
            async (response) => {
                return await response.json().then(
                    (rt) => {
                        //console.log("Fetched the following UTG:")
                        //console.log(rt)
                        if (rt.serviceView.length < 1) {
                            console.error("UTG not found for the current BE")
                            return null
                        }
                        return rt.serviceView[0]
                    }
                )
            }
        )
    },
    /**
     * Export UTG List
     * @param {string} businessEntityId - The Business Entity ID
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The update result as a Promise
     */
    exportUtgList: async (businessEntityId, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/components/query/csv`
        const tasksFilter = '{"Predicates": []}'
        const body = JSON.stringify(tasksFilter).replace("\n", "")
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                body: body
            },
            async (response) => {
                return await response.arrayBuffer()
            }
        )
    },
    /**
     * Update Component
     * @param {string} businessEntityId - The Business Entity ID
     * @param {string} component - The utg/component object. This object contains businessEntityId, machinePermanentId, and componentId
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The update result as a Promise
     */
    updateComponent: async (businessEntityId, component, config, accessToken) => {
        if (!businessEntityId) {
            throw new Error("utgApi.updateComponent: business entity ID is undefined")
        }
        else if (!config) {
            throw Error("utgApi.updateComponent: config is undefined")
        }
        else if (!config.managementApiUrl) {
            throw Error("utgApi.updateComponent: config.managementApiUrl is undefined")
        }
        else if (!component) {
            throw Error("utgApi.updateComponent: component is undefined")
        }
        else if (!component.machine) {
            console.error(component)
            throw Error("utgApi.updateComponent: component.machine is undefined")
        }
        const permanentId = component.machine.permanentId
        if (!permanentId) {
            throw Error("utgApi.updateComponent: component.machine.permanentId is undefined")
        }
        const id = component.id
        if (!id) {
            throw Error("utgApi.updateComponent: component.id is undefined")
        }
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/machines/${permanentId}/components/${id}`
        // IMPORTANT: The method is POST but header tells server to change this to PUT to match the previous behavior in emberjs
        const body = JSON.stringify(component)
        return await apiClient(
            url,
            accessToken,
            {
            method: "POST",
            headers: {
                'X-HTTP-Method-Override': 'PUT',
            },
            body: body
            },
            async (response) => {
                return await response.json().then(
                    (result) => {
                        //console.log(result)
                        return result.component
                    }
                )
            }
        )
    },
    /**
     * Remove UTG from command center
     * @param {string} businessEntityId - The Business Entity ID
     * @param {string} machinePermanentId - The globally unique machine permanent Id
     * @param {object} config - The application config object
     * @param {object} accessToken - The logged in user's accessToken
     * @returns {Promise} - The result as a Promise
     */
    deleteUtg: async (businessEntityId, machinePermanentId, componentPermanentId, config, accessToken) => {
        const url = `${config.managementApiUrl}/businessentities/${businessEntityId}/machines/${machinePermanentId}`
        // NOTE that this actually inactivates the machine record that the UTG is connected to
        // IMPORTANT: Note the server requires POST with a header override to indicate DELETE
        return await apiClient(
            url,
            accessToken,
            {
                method: "POST",
                headers: {
                    'X-HTTP-Method-Override': 'DELETE',
                    'clientId': '4'
                }
            },
            (response) => {
                return response
            }
        )
    }
}